import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Users, Plus, Search, Building2, Phone, Mail, Calendar,
  MoreHorizontal, Edit, Trash2, CheckCircle, Loader2, HelpCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// Tranches de budget
const BUDGET_RANGES = [
  { value: 'unknown', label: "Je ne sais pas", amount: 0 },
  { value: '0-1000', label: "Moins de 1 000€", amount: 500 },
  { value: '1000-3000', label: "1 000€ - 3 000€", amount: 2000 },
  { value: '3000-5000', label: "3 000€ - 5 000€", amount: 4000 },
  { value: '5000-10000', label: "5 000€ - 10 000€", amount: 7500 },
  { value: '10000-20000', label: "10 000€ - 20 000€", amount: 15000 },
  { value: '20000+', label: "Plus de 20 000€", amount: 25000 },
];

const AffiliateLeadsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState({ total: 0, converted: 0, pending: 0, total_commission: 0 });

  const [newLead, setNewLead] = useState({
    client_name: '', client_email: '', phone: '', service: '', budget_range: 'unknown', notes: ''
  });

  const statusConfig = {
    'new': { color: 'bg-blue-100 text-blue-700', label: 'Nouveau' },
    'contacted': { color: 'bg-amber-100 text-amber-700', label: 'Contacté' },
    'qualified': { color: 'bg-purple-100 text-purple-700', label: 'Qualifié' },
    'pending': { color: 'bg-slate-100 text-slate-700', label: 'En attente' },
    'converted': { color: 'bg-emerald-100 text-emerald-700', label: 'Converti' },
    'won': { color: 'bg-emerald-100 text-emerald-700', label: 'Gagné' },
    'lost': { color: 'bg-red-100 text-red-700', label: 'Perdu' },
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const res = await api.get('/affiliates/me/leads');
      setLeads(res.data?.leads || []);
      setSummary(res.data?.summary || { total: 0, converted: 0, pending: 0 });
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.client_name || !newLead.service) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    // Get estimated value from budget range
    const budgetRange = BUDGET_RANGES.find(b => b.value === newLead.budget_range);
    const estimatedValue = budgetRange ? budgetRange.amount : 0;
    
    setCreating(true);
    try {
      await api.post('/affiliates/me/leads', {
        client_name: newLead.client_name,
        client_email: newLead.client_email,
        phone: newLead.phone,
        service: newLead.service,
        estimated_value: estimatedValue,
        budget_range: newLead.budget_range,
        notes: newLead.notes
      });
      
      toast.success('Lead ajouté avec succès !');
      setNewLead({ client_name: '', client_email: '', phone: '', service: '', budget_range: 'unknown', notes: '' });
      setShowAddModal(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout du lead');
    } finally {
      setCreating(false);
    }
  };

  const filteredLeads = leads.filter(lead => 
    (lead.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.service || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8", collapsed ? "md:ml-20" : "md:ml-64")}
        style={{ paddingTop: "130px" }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Mes Leads</h1>
            <p className="text-slate-500">{leads.length} lead(s) au total</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
            <Plus className="w-4 h-4 mr-2" /> Ajouter un lead
          </Button>
        </div>

        <Card className="mb-6 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher un lead..." 
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-4">Aucun lead trouvé</p>
            <Button onClick={() => setShowAddModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
              <Plus className="w-4 h-4 mr-2" /> Ajouter votre premier lead
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLeads.map((lead) => (
              <Card key={lead.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold" style={{ color: theme.colors?.primary }}>{lead.client_name}</h3>
                        <p className="text-sm text-slate-500">{lead.service}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          {lead.client_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{lead.client_email}</span>}
                          {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        {/* Display budget - check for budget_range, budget_min/budget_max, OR estimated_value */}
                        {(() => {
                          // First check if we have budget_min/budget_max
                          if (lead.budget_min || lead.budget_max) {
                            const min = lead.budget_min ? lead.budget_min.toLocaleString('fr-FR') : '?';
                            const max = lead.budget_max ? lead.budget_max.toLocaleString('fr-FR') : '?';
                            return (
                              <p className="font-medium text-emerald-600">
                                {lead.budget_min && lead.budget_max 
                                  ? `${min}€ - ${max}€`
                                  : lead.budget_min 
                                    ? `À partir de ${min}€`
                                    : `Jusqu'à ${max}€`}
                              </p>
                            );
                          }
                          // Then check for budget_range
                          if (lead.budget_range && lead.budget_range !== 'unknown') {
                            return (
                              <p className="font-medium text-emerald-600">
                                {BUDGET_RANGES.find(b => b.value === lead.budget_range)?.label || 
                                 `${(lead.estimated_value || 0).toLocaleString('fr-FR')}€`}
                              </p>
                            );
                          }
                          // Check for estimated_value
                          if (lead.estimated_value && lead.estimated_value > 0) {
                            return (
                              <p className="font-medium text-emerald-600">
                                ~{lead.estimated_value.toLocaleString('fr-FR')}€
                              </p>
                            );
                          }
                          // Fallback - no budget info
                          return (
                            <p className="text-sm text-slate-400 flex items-center gap-1">
                              <HelpCircle className="w-3 h-3" />
                              Budget non défini
                            </p>
                          );
                        })()}
                        {lead.commission_amount > 0 && (
                          <p className="text-xs text-slate-500">Commission: {lead.commission_amount.toLocaleString('fr-FR')}€</p>
                        )}
                      </div>
                      <Badge className={statusConfig[lead.status]?.color || 'bg-slate-100 text-slate-700'}>
                        {statusConfig[lead.status]?.label || lead.status}
                      </Badge>
                      <span className="text-sm text-slate-400">{lead.created_at?.split('T')[0]}</span>
                    </div>
                  </div>
                  {lead.notes && (
                    <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">{lead.notes}</div>
                  )}
                  {lead.is_client && (
                    <div className="mt-2 p-2 bg-emerald-50 rounded text-sm text-emerald-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Client converti{lead.first_project_name && ` - Projet: ${lead.first_project_name}`}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Lead Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ajouter un nouveau lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Entreprise / Contact *</Label>
                <Input value={newLead.client_name} onChange={(e) => setNewLead({...newLead, client_name: e.target.value})} placeholder="Nom de l'entreprise ou du contact" />
              </div>
              <div>
                <Label>Service recherché *</Label>
                <Input value={newLead.service} onChange={(e) => setNewLead({...newLead, service: e.target.value})} placeholder="Site web, Logo, etc." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input value={newLead.client_email} onChange={(e) => setNewLead({...newLead, client_email: e.target.value})} type="email" placeholder="email@exemple.com" />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={newLead.phone} onChange={(e) => setNewLead({...newLead, phone: e.target.value})} placeholder="06 12 34 56 78" />
                </div>
              </div>
              <div>
                <Label>Budget estimé</Label>
                <Select 
                  value={newLead.budget_range} 
                  onValueChange={(value) => setNewLead({...newLead, budget_range: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une tranche de budget" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        <div className="flex items-center gap-2">
                          {range.value === 'unknown' && <HelpCircle className="w-4 h-4 text-slate-400" />}
                          {range.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Sélectionnez "Je ne sais pas" si le prospect n'a pas indiqué de budget
                </p>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={newLead.notes} onChange={(e) => setNewLead({...newLead, notes: e.target.value})} placeholder="Informations complémentaires..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
              <Button onClick={handleAddLead} disabled={creating} style={{ backgroundColor: theme.colors?.accent }}>
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AffiliateLeadsPage;